/**
 * -----------------------------------------------------------------------------
 * Geozone Category Listing Page
 * -----------------------------------------------------------------------------
 * This page is used to list all the geozone categories of the organization.
 */

import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useDebouncedCallback } from "use-debounce";
import { useOrganizationGeozoneCategoriesQuery } from "../../../api/network/adminApiServices";
import {
  TListingQueryParams,
  TModalsState,
  setListingQueryParams,
  setModalsData,
} from "../../../api/store/commonSlice";
import { FilterType } from "../../../api/types/Admin";
import { TLegacyGeozoneCategory } from "../../../api/types/GeozoneCategory";
import { TSelectboxOption } from "../../../components/admin/formFields";
import HeaderView from "../../../components/admin/headerView";
import AdminTable from "../../../components/adminTable";
import ListingPageSubHeader from "../../../components/listingPageSubHeader";
import ListingTableHeader from "../../../components/listingTableHeader";
import {
  EditListingColumnsButton,
  ModalEditListingColumns,
  TListingColumn,
} from "../../../components/modalEditListingColumns";
import Pagination, {
  TPaginationSelected,
} from "../../../components/pagination";
import { TAdminTableRowData } from "../../../components/types";
import { APP_CONFIG } from "../../../constants/constants";
import { ACL_NAMES, useACL } from "../../../hooks/acl";
import { useListingFilters } from "../../../hooks/listingFilter";
import { useAccountTimezone } from "../../../hooks/timezone";
import { routeUrls } from "../../../navigation/routeUrls";
import { SortingStatusWithReset } from "../../../utils/adminListings";
import {
  localStorageKeys,
  useLocalStorage,
} from "../../../utils/localStorageItems";
import { useLoggedInUserData } from "../../../utils/user";
import { ROLE_ACCESS_LEVELS } from "../../admin-details/role/constants";
import AdminsGeozoneCategoriesCreateNew from "./createNewGeozoneCategory";

const all_columns = [
  // "Sr. No",
  "Geozone Category ID",
  "Display Name",
  "Description",
  "Total Geozones",
];

const columnOrderingFields: Record<string, string> = {
  "Geozone Category ID": "categoryid",
  "Display Name": "displayname",
  Description: "description",
  "Total Geozones": "geozones_count",
};

const ScreenDashboardAdminGeozoneCategories = () => {
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.geozone_categories",
  });
  const { t: tMain } = useTranslation();
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const { geozoneCategories: orgGeozoneCategoriesQueryParams } =
    listingQueryParams;
  const dispatch = useDispatch();
  const listingWrapperRef = React.useRef<HTMLDivElement>(null);

  const { getUserACLAccessLevel } = useACL();

  // columns
  const { getLocalStorageItem } = useLocalStorage();
  const savedColumns = getLocalStorageItem(
    localStorageKeys.columns.GeozoneCategories
  );
  const [columns, setColumns] = React.useState<TListingColumn[]>(
    all_columns.map((colItem, _) => {
      return {
        name: colItem,
        show: !!savedColumns ? savedColumns.includes(colItem) : true,
      };
    })
  );

  // retrieve account timezone
  const { tzForDateFns } = useAccountTimezone();

  // filter mechainism
  const { filters } = useListingFilters();
  const [activeFilterSlug, setActiveFilterSlug] = React.useState<FilterType>(
    filters?.[0]?.slug
  );

  // updating query params on filter change
  useEffect(() => {
    const newParams = {
      ...listingQueryParams,
      geozone_categories: {
        ...orgGeozoneCategoriesQueryParams,
        isactive: undefined,
        page: 1,
        ...(activeFilterSlug === "both"
          ? {}
          : {
              isactive: activeFilterSlug === "active" ? 1 : 0,
            }),
      },
    };
    dispatch(setListingQueryParams(newParams));
  }, [activeFilterSlug]);

  // preparing query params
  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        geozoneCategories: {
          ...orgGeozoneCategoriesQueryParams,
          page: 1,
          search: value,
        },
      })
    );
  }, 500);

  const handleOrderingChange = (ordering?: string) => {
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        geozoneCategories: {
          ...orgGeozoneCategoriesQueryParams,
          ordering,
          page: 1,
        },
      })
    );
  };

  // fetching geozone categories data
  const {
    data: dataOrgGeozoneCategories,
    isFetching: isFetchingOrgGeozoneCategories,
  } = useOrganizationGeozoneCategoriesQuery(
    {
      ...orgGeozoneCategoriesQueryParams,
      // 'search_fields' intentionally not added here as default search fields are defined in the API service
    },
    {
      skip: !thisUserOrganizationId,
    }
  );
  const { count, next, previous, results } = dataOrgGeozoneCategories || {};

  // smooth scroll listing to the top on page change
  useEffect(() => {
    listingWrapperRef?.current?.scrollTo({ behavior: "smooth", top: 0 });
  }, [dataOrgGeozoneCategories]);

  // preparing table data
  const tableData: TAdminTableRowData[] = !!results
    ? (results || ([] as TLegacyGeozoneCategory[])).map(
        (item: TLegacyGeozoneCategory, index: number) => ({
          navLink: `${routeUrls.dashboardChildren.adminChildren.geozoneCategories}/${item.categoryid}`,
          cellData: [
            item?.categoryid, // id:
            item?.displayname, // displayname:
            item?.description || "-", // description:
            item?.geozones.length || "0", // total geozones:
            // true ? (
            //   <span className="text-field-success">{tMain("yes")}</span>
            // ) : false ? (
            //   <span className="text-field-error-dark">{tMain("no")}</span>
            // ) : (
            //   "-"
            // ), // active:
          ].filter((_, index) => columns[index].show),
          isActive: true, // item?.isactive === 1,
        })
      )
    : [];

  // rendering
  return (
    <>
      <HeaderView title={t("heading")} />
      <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} pt-4`}>
        <ListingPageSubHeader
          heading={t("sub_heading")}
          {...(getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_GEOZONE) >=
            ROLE_ACCESS_LEVELS.ALL && {
            buttonText: t("add_new"),
            buttonCallback: () => {
              dispatch(
                setModalsData({
                  ...modalsState,
                  showCreateGeozoneCategory: true,
                })
              );
            },
          })}
        />
        <ListingTableHeader
          heading={
            <div className="flex items-center gap-4">
              {/* Heading */}
              <span>{t("listing_heading")}</span>

              {/* Sorted by label */}
              {!!orgGeozoneCategoriesQueryParams?.ordering && (
                <SortingStatusWithReset
                  ordering={orgGeozoneCategoriesQueryParams.ordering}
                  columnOrderingFields={columnOrderingFields}
                  onResetOrdering={() => handleOrderingChange(undefined)}
                />
              )}
            </div>
          }
          searchBoxPlaceholder={t("search_placeholder")}
          searchBoxValue={orgGeozoneCategoriesQueryParams.search}
          searchBoxOnChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            debouncedSetSearchKeyword(e.target.value)
          }
          searchBoxOnClear={() => debouncedSetSearchKeyword("")}
          // we don't want filters on this page
          // filters={filters}
          // activeFilterSlug={activeFilterSlug}
          // handleFilterChange={(slug) => setActiveFilterSlug(slug)}
        />
        <ModalEditListingColumns
          columns={columns}
          setColumns={setColumns}
          lsKey={localStorageKeys.columns.GeozoneCategories}
        />
        <div className="py-4 mt-4 relative">
          <EditListingColumnsButton />
          <AdminTable
            viewportHeightOffset={"400"}
            columns={columns
              .filter((item) => item.show)
              .map((item) => item.name)}
            data={tableData}
            isLoading={isFetchingOrgGeozoneCategories}
            listingQueryParams={orgGeozoneCategoriesQueryParams}
            listingWrapperRef={listingWrapperRef}
            onOrderingChange={handleOrderingChange}
            ordering={orgGeozoneCategoriesQueryParams.ordering}
            sortableColumns={columnOrderingFields}
          />
          {!isFetchingOrgGeozoneCategories && (
            <Pagination
              pageSize={orgGeozoneCategoriesQueryParams.page_size}
              handlePageSizeChange={(e: TSelectboxOption | null) => {
                dispatch(
                  setListingQueryParams({
                    ...listingQueryParams,
                    geozoneCategories: {
                      ...orgGeozoneCategoriesQueryParams,
                      page: 1,
                      page_size: parseInt(`${e?.value}`),
                    },
                  })
                );
              }}
              totalPages={
                count
                  ? Math.ceil(count / orgGeozoneCategoriesQueryParams.page_size)
                  : 1
              }
              forcePage={orgGeozoneCategoriesQueryParams.page - 1}
              handlePageClick={(data: TPaginationSelected) => {
                dispatch(
                  setListingQueryParams({
                    ...listingQueryParams,
                    geozoneCategories: {
                      ...orgGeozoneCategoriesQueryParams,
                      page: data?.selected + 1,
                    },
                  })
                );
              }}
              paginCurrentPage={orgGeozoneCategoriesQueryParams.page}
              paginItemsOnCurrentPage={results?.length || 0}
              paginTotalItemsCount={count}
            />
          )}
        </div>
      </div>
      <AdminsGeozoneCategoriesCreateNew />
    </>
  );
};

export default ScreenDashboardAdminGeozoneCategories;
